"use client";

// FILE: src/features/nmck-calculator/components/chop-request-step.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT:
// PURPOSE: Step 4 «Запрос КП у ЧОП» — 3 карточки ЧОП, форма контактов заказчика, отправка запросов
// SCOPE: Загрузка ЧОП из /api/calculator/chops, замена через поиск, отправка через POST /api/calculator/send-requests
// INPUT: calculationId (UUID сохранённого расчёта, обязателен для отправки), nmckTotal, posts, period, region, onBack
// OUTPUT: Состояния idle/loading/sending/sent/error
// KEYWORDS: DOMAIN(NMCC): ChopRequest; TECH(React): ClientComponent; STATE(idle|loading|sending|sent|error)
// END_MODULE_CONTRACT

import { useState, useEffect } from "react";
import { Loader2, Send, CheckCircle2, XCircle, ArrowLeft, Mail, Building2, RefreshCw, Plus } from "lucide-react";
import { Button } from "@oxpa/shared/ui/button";
import { Input } from "@oxpa/shared/ui/input";
import { ChopCard } from "./chop-card";
import { ChopSearchModal } from "./chop-search-modal";
import type { ChopCandidate } from "@/app/api/calculator/chops/route";

type StepState = "idle" | "loading" | "sending" | "sent" | "error";

interface ChopRequestStepProps {
  calculationId?: string;
  nmckTotal?: number;
  posts?: string;
  period?: string;
  region?: string;
  onBack?: () => void;
}

export function ChopRequestStep({ calculationId, nmckTotal, posts, period, region, onBack }: ChopRequestStepProps) {
  const [chops, setChops] = useState<ChopCandidate[]>([]);
  const [state, setState] = useState<StepState>("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [replaceIndex, setReplaceIndex] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [results, setResults] = useState<Array<{ chop: string; email: string; success: boolean; error?: string }>>([]);

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isEmailValid = EMAIL_RE.test(customerEmail);
  const isFormValid = customerName.trim().length > 0 && customerEmail.trim().length > 0 && isEmailValid;

  /* <MEMENTO>2026-08-21: было useEffect(..., [state]) с guard state === 'idle': после успешной загрузки
  state возвращался в 'idle', эффект срабатывал снова → бесконечный цикл fetch /api/calculator/chops
  (замерено: 25 запросов за 25 с), UI навечно вис на «Подбор ЧОП...». Грузим один раз при монтировании
  (Radix TabsContent перемонтирует панель при повторном входе на шаг — подбор обновится).</MEMENTO> */
  useEffect(() => {
    loadChops();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadChops() {
    setState("loading");
    try {
      const res = await fetch("/api/calculator/chops");
      const json = await res.json();
      if (json.success) {
        setChops(json.data as ChopCandidate[]);
        setState("idle");
      } else {
        setErrorMsg(json.error || "Ошибка загрузки ЧОП");
        setState("error");
      }
    } catch {
      setErrorMsg("Не удалось загрузить список ЧОП");
      setState("error");
    }
  }

  function handleRemove(index: number) {
    setChops((prev) => prev.filter((_, i) => i !== index));
  }

  function handleReplace(index: number) {
    setReplaceIndex(index);
  }

  function handleReplaceSelect(newChop: ChopCandidate) {
    if (isAdding) {
      setChops((prev) => [...prev, { ...newChop, is_fixed: false }]);
      setIsAdding(false);
    } else if (replaceIndex !== null) {
      setChops((prev) => {
        const next = [...prev];
        next[replaceIndex] = { ...newChop, is_fixed: false };
        return next;
      });
      setReplaceIndex(null);
    }
  }

  function handleAddChop() {
    setIsAdding(true);
  }

  async function handleSend() {
    // <MEMENTO>2026-08-21: calculation_id — UUID реально сохранённого расчёта (nmtck_chop_requests.calculation_id
    // — FK NOT NULL). Псевдо-id calc_<timestamp> ронял INSERT истории; поэтому без calculationId отправка
    // запрещена: пользователь возвращается на шаг 3 и жмёт «Запрос КП у ЧОП» (или «Сохранить»).</MEMENTO>
    if (!calculationId) {
      setErrorMsg('Расчёт не сохранён: вернитесь на шаг 3 и повторите');
      setState("error");
      return;
    }
    setState("sending");
    try {
      const body = {
        calculation_id: calculationId,
        selected_chops: chops.map((c) => ({
          id: c.id,
          email: c.contact_email || "",
          name: c.name_short,
        })),
        customer_contacts: {
          name: customerName,
          email: customerEmail,
          phone: customerPhone || undefined,
        },
        nmck: nmckTotal?.toString(),
        posts,
        period,
        region,
      };

      const res = await fetch("/api/calculator/send-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = await res.json();

      if (json.success) {
        const rawResults = json.results || [];
        setResults(rawResults.map((r: any) => ({
          // <MEMENTO>2026-08-21: route отдаёт chop_name — без него в UI уезжал UUID ЧОПа (запрещено правилами проекта)</MEMENTO>
          chop: r.chop || r.chop_name || r.name || r.chop_id || "—",
          email: r.email || "",
          success: r.status === "sent" || r.success === true,
          error: r.error || r.message || undefined,
        })));
        setState("sent");
      } else {
        setErrorMsg(json.error || "Ошибка отправки");
        setState("error");
      }
    } catch {
      setErrorMsg("Не удалось отправить запросы. Проверьте соединение.");
      setState("error");
    }
  }

  if (state === "loading") {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-[hsl(var(--portal-text-muted))]">
        <Loader2 className="h-8 w-8 animate-spin mb-4" />
        <p className="text-xs uppercase tracking-widest font-mono">Подбор ЧОП...</p>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <XCircle className="h-8 w-8 text-[hsl(var(--portal-danger))] mb-4" />
        <p className="text-sm text-[hsl(var(--portal-danger))] font-mono mb-4">{errorMsg}</p>
        <Button
          type="button"
          variant="outline"
          onClick={loadChops}
          className="rounded-none border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] hover:text-[hsl(var(--portal-text))] h-10 px-4 text-[10px] uppercase tracking-widest"
        >
          <RefreshCw className="h-3 w-3 mr-1.5" />
          Повторить
        </Button>
      </div>
    );
  }

  if (state === "sent") {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center space-y-4">
        <div className="rounded-full bg-[hsl(var(--portal-accent))]/10 border border-[hsl(var(--portal-accent))]/30 p-4">
          <CheckCircle2 className="h-10 w-10 text-[hsl(var(--portal-accent))]" />
        </div>
        <h3 className="text-lg font-bold text-[hsl(var(--portal-text))] font-mono">Запросы отправлены</h3>
        <p className="text-sm text-[hsl(var(--portal-text-secondary))] max-w-md font-mono">
          Письма направлены в {chops.map((c) => c.name_short).join(", ")}.
          Ожидайте ответа на указанный email.
        </p>
        {results.length > 0 && (
          <div className="w-full max-w-md space-y-1 pt-2">
            {results.map((r, i) => (
              <div
                key={i}
                data-testid="chop-status"
                className={`flex items-center gap-2 text-xs font-mono p-2 border ${
                  r.success
                    ? "text-[hsl(var(--portal-accent))] border-[hsl(var(--portal-accent))]/20 bg-[hsl(var(--portal-accent))]/5"
                    : "text-[hsl(var(--portal-danger))] border-[hsl(var(--portal-danger))]/20 bg-[hsl(var(--portal-danger))]/5"
                }`}
              >
                {r.success ? (
                  <CheckCircle2 className="h-3 w-3 shrink-0" />
                ) : (
                  <XCircle className="h-3 w-3 shrink-0" />
                )}
                <span className="truncate">{r.chop}: {r.success ? "Отправлено" : r.error || "Ошибка"}</span>
              </div>
            ))}
          </div>
        )}
        <div className="space-y-1 text-xs text-[hsl(var(--portal-text-muted))] font-mono">
          <p><Mail className="h-3 w-3 inline mr-1" />{customerEmail}</p>
        </div>
        {onBack && (
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="rounded-none border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text-secondary))] hover:text-[hsl(var(--portal-text))] h-10 px-4 text-[10px] uppercase tracking-widest mt-4"
          >
            <ArrowLeft className="h-3 w-3 mr-1.5" />
            Назад к результату
          </Button>
        )}
      </div>
    );
  }

  const excludeIds = chops.map((c) => c.id);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 border-b border-[hsl(var(--portal-border))] pb-4">
        <Building2 className="h-5 w-5 text-[hsl(var(--portal-text))]" />
        <h3 className="text-sm uppercase tracking-widest text-[hsl(var(--portal-text))] font-bold">
          Шаг 4: Запрос коммерческих предложений
        </h3>
      </div>

      <div className="space-y-2">
        <p className="text-xs text-[hsl(var(--portal-text-muted))] font-mono">
          Система подобрала ЧОП из рейтинга. Вы можете заменить любую организацию.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {chops.map((chop, i) => (
            <ChopCard
              key={`${chop.id}-${i}`}
              chop={chop}
              onReplace={() => handleReplace(i)}
              onRemove={chops.length > 1 ? () => handleRemove(i) : undefined}
              disabled={state === "sending"}
            />
          ))}
        </div>
        {chops.length < 5 && (
          <div className="flex justify-center pt-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddChop}
              disabled={state === "sending"}
              className="rounded-none border-dashed border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text-muted))] hover:text-[hsl(var(--portal-text))] hover:border-[hsl(var(--portal-border-hover))] h-9 px-5 text-[10px] uppercase tracking-widest"
            >
              <Plus className="h-3 w-3 mr-1.5" />
              Добавить ЧОП
            </Button>
          </div>
        )}
      </div>

      <div className="border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-surface))] p-4 space-y-4">
        <h4 className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-secondary))] font-bold">
          <Mail className="h-3 w-3 inline mr-1.5" />
          Контакты заказчика
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))] block mb-1">
              Имя / Организация
            </label>
            <Input
              name="customer_name"
              placeholder="Иванов Иван"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              disabled={state === "sending"}
              className="rounded-none bg-[hsl(var(--portal-bg))] border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] font-mono h-10"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))] block mb-1">
              Email *
            </label>
            <Input
              name="customer_email"
              type="email"
              placeholder="ivanov@example.com"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              disabled={state === "sending"}
              className={`rounded-none bg-[hsl(var(--portal-bg))] border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] font-mono h-10 ${customerEmail && !isEmailValid ? "border-[hsl(var(--portal-danger))]" : ""}`}
            />
            {customerEmail && !isEmailValid && (
              <p className="text-[10px] text-[hsl(var(--portal-danger))] mt-1">Ошибка: неверный формат email</p>
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))] block mb-1">
              Телефон (опционально)
            </label>
            <Input
              name="customer_phone"
              type="tel"
              placeholder="+7 (999) 123-45-67"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              disabled={state === "sending"}
              className="rounded-none bg-[hsl(var(--portal-bg))] border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] font-mono h-10"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-2">
        {onBack ? (
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            disabled={state === "sending"}
            className="rounded-none border-[hsl(var(--portal-border-hover))] bg-transparent text-[hsl(var(--portal-text))] hover:bg-[hsl(var(--portal-surface-2))] h-12 w-12 p-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        ) : <div />}
        <Button
          type="button"
          disabled={!isFormValid || state === "sending" || chops.length === 0 || !calculationId}
          title={!calculationId ? "Расчёт не сохранён — вернитесь на шаг 3 и нажмите «Запрос КП у ЧОП» или «Сохранить»" : undefined}
          onClick={handleSend}
          className="rounded-none bg-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent-on))] hover:bg-[hsl(var(--portal-accent))] disabled:bg-[hsl(var(--portal-surface-2))] disabled:text-[hsl(var(--portal-text-dim))] uppercase tracking-widest text-[10px] h-12 px-8 font-bold"
        >
          {state === "sending" ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Отправка...</>
          ) : (
            <><Send className="mr-2 h-4 w-4" /> Отправить запросы</>
          )}
        </Button>
      </div>

      <ChopSearchModal
        open={replaceIndex !== null || isAdding}
        onOpenChange={(open) => { if (!open) { setReplaceIndex(null); setIsAdding(false); } }}
        onSelect={handleReplaceSelect}
        excludeIds={excludeIds}
      />
    </div>
  );
}

// trigger deploy 2026-07-02